import { useEffect, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../../lib/tokens";

interface Props {
  label: string;
  value: string;
  onChange: (next: string) => void;
  error?: string | null;
}

const HEX6 = /^#[0-9A-F]{6}$/i;

export function ColorPickerField({ label, value, onChange, error }: Props) {
  const [open, setOpen] = useState(false);
  const [textBuf, setTextBuf] = useState(value);
  const [lastValue, setLastValue] = useState(value);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Re-sync the local text buffer whenever the parent-controlled `value`
  // changes (e.g. after a successful save resets the form). This is the
  // canonical React pattern for derived state — see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (value !== lastValue) {
    setLastValue(value);
    setTextBuf(value);
  }

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const commitText = (next: string) => {
    setTextBuf(next);
    if (HEX6.test(next)) onChange(next.toUpperCase());
  };

  return (
    <div style={{ marginBottom: space.md }}>
      <span style={{ display: "block", fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.stone, marginBottom: 6 }}>
        {label}
      </span>
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={`${label} swatch`}
          style={{
            width: 32, height: 32, padding: 0,
            background: HEX6.test(value) ? value : "#FFF",
            border: `1px solid ${colors.fog}`, borderRadius: radius.sm,
            cursor: "pointer",
          }}
        />
        <input
          type="text"
          value={textBuf}
          onChange={(e) => commitText(e.target.value)}
          spellCheck={false}
          style={{
            width: 110, height: 32, padding: "0 10px",
            background: colors.white, border: `1px solid ${error ? colors.error : colors.fog}`,
            borderRadius: radius.sm, fontSize: fontSize.md, color: colors.ink,
            fontFamily: "JetBrains Mono, monospace", outline: "none",
          }}
        />
        {open && (
          <div
            ref={popRef}
            style={{
              position: "absolute", top: 40, left: 0, zIndex: 10,
              padding: 8, background: colors.white,
              border: `1px solid ${colors.fog}`, borderRadius: radius.md,
              boxShadow: shadow.md,
            }}
          >
            <HexColorPicker color={HEX6.test(value) ? value : "#000000"} onChange={(c) => onChange(c.toUpperCase())} />
          </div>
        )}
      </div>
      {error && (
        <span style={{ display: "block", fontSize: fontSize.sm, color: colors.error, marginTop: 4 }}>{error}</span>
      )}
    </div>
  );
}
